import React, { createContext, useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import TokenService from 'services/TokenService';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [notification, setNotification] = useState(null);
  const location = useLocation();
  const currentPathRef = useRef(location.pathname);

  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  const dismiss = useCallback(() => {
    setNotification(null);
  }, []);

  useEffect(() => {
    const authDataSubscription = TokenService.authDataChanges$.subscribe(
      (authData) => {
        if (!authData) {
          dismiss();
          toast.dismiss();
        }
      });
    return () => authDataSubscription.unsubscribe();
  }, [dismiss]);

  useEffect(() => {
    const show = (type) => (content) => {
      const id = Date.now() + Math.random();
      setNotification({ 
        id, 
        type, 
        content,
        path: currentPathRef.current,
        routeChanges: 0
      });
      return id;
    };

    const origInfo    = toast.info;
    const origSuccess = toast.success;
    const origWarn    = toast.warn;
    const origError   = toast.error;
    const origDismiss = toast.dismiss;

    toast.info    = show('info');
    toast.success = show('success');
    toast.warn    = show('warning');
    toast.error   = show('error');
    toast.dismiss = (id) => {
      dismiss();
      if (origDismiss) origDismiss(id);
    };

    return () => {
      toast.info    = origInfo;
      toast.success = origSuccess;
      toast.warn    = origWarn;
      toast.error   = origError;
      toast.dismiss = origDismiss;
    };
  }, [dismiss]);

  const contextValue = useMemo(() => ({ notification, dismiss }), [notification, dismiss]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
